import * as Sentry from '@sentry/react';
import version from 'version';
import { env } from 'constants/env';
import { identify } from 'V2/Utilities/Identify';
import { EntityType, FieldDataType, PropertiesType } from 'V2/Types';
import { FullStory } from './Analytics';

type SetAnalyticsType = (attributionData: PropertiesType) => void;

export const formatAttributionData = (
  attributionData: PropertiesType,
  defaultValue: string = ''
) => {
  return Object.keys(attributionData).reduce((data, key) => {
    if (key === 'current_user') return data;

    return {
      ...data,
      [`new_${key}`]: attributionData[key] ?? defaultValue,
    };
  }, {});
};

export const setAnalytics: SetAnalyticsType = (attributionData) => {
  const VERSION =
    env.nodeEnv === 'development' ? 'development' : version.git_sha;

  // we need to match what is currently in FullStory for
  // event properties on the client; we should be using
  // a pass through from the server instead of mapping
  // it in 2 places; we will tackle this down the line
  const prefixedData = formatAttributionData(attributionData);

  Sentry.setUser({
    id: attributionData.cc_uuid as string,
  });

  Sentry.setTags({
    version: VERSION,
    attribution_link_click_id: attributionData.attribution_link_click_id,
    marketing_id: attributionData.marketing_id,
    insurance_application_id: attributionData.insurance_application_id,
  });

  FullStory.setUserVars({
    version: VERSION,
    ...prefixedData,
  });

  if (!sessionStorage.marketingId && attributionData.marketing_id) {
    const marketingId = `${attributionData.marketing_id}`;

    sessionStorage.setItem('marketingId', marketingId);
    identify(marketingId);
  }
};

export const getStepCompleteInfo = async ({
  summary: { entities },
  step_name: stepName,
  field_data: fieldData,
}: {
  summary: { entities: EntityType[] };
  step_name: string;
  field_data: FieldDataType[];
}) => {
  const queryParams = new URLSearchParams(window.location.search);
  const isEditMode = queryParams.get('edit') === 'true';

  const isDriverStep = (stepName: string) => {
    return stepName.includes('driver');
  };
  const isVehicleStep = (stepName: string) => {
    return stepName.includes('vehicle');
  };

  if (isEditMode || (!isDriverStep(stepName) && !isVehicleStep(stepName))) {
    return null;
  }

  const driverEntities = entities.filter(
    (entity) =>
      entity.type.id === 'driver' && entity.type.sub_type !== 'named_insured'
  );
  const vehicleEntities = entities.filter(
    (entity) => entity.type.id === 'vehicle'
  );

  const groupedFieldData = fieldData
    .filter(
      ({ entity_type }) =>
        entity_type.id === 'driver' || entity_type.id === 'vehicle'
    )
    .reduce(
      (acc, item) => {
        const entityType = item.entity_type.id;
        const entityId = item.entity_id;

        if (!acc[entityType]) {
          acc[entityType] = {};
        }

        if (!acc[entityType][entityId]) {
          acc[entityType][entityId] = [];
        }

        acc[entityType][entityId].push(item);

        return acc;
      },
      {} as Record<string, Record<string, FieldDataType[]>>
    );

  const defaultResult = () => ({
    vehicleNum: 'none',
    driverNum: 'none',
    totalVehicles: vehicleEntities.length,
    totalDrivers: driverEntities.length,
  });

  const driverFieldData = groupedFieldData['driver'] || {};
  const vehicleFieldData = groupedFieldData['vehicle'] || {};

  const driverFieldDataCount = Object.keys(driverFieldData).length;
  const vehicleFieldDataCount = Object.keys(vehicleFieldData).length;

  if (
    driverFieldDataCount > 1 ||
    vehicleFieldDataCount > 1 ||
    (vehicleFieldDataCount === 1 && driverFieldDataCount === 1)
  ) {
    return defaultResult();
  }

  const FIRST_FIELD_DATA_INDEX = 0;
  let selectedField: FieldDataType | undefined;

  if (vehicleFieldDataCount === 1) {
    const entityId = Object.keys(vehicleFieldData)[0];

    selectedField = vehicleFieldData[entityId][FIRST_FIELD_DATA_INDEX];
  } else if (driverFieldDataCount === 1) {
    const entityId = Object.keys(driverFieldData)[0];

    selectedField = driverFieldData[entityId][FIRST_FIELD_DATA_INDEX];
  }

  if (!selectedField) {
    return defaultResult();
  }

  const {
    entity_type: { id: selectedEntityType },
    entity_id: selectedEntityId,
  } = selectedField;

  if (selectedEntityType !== 'driver' && selectedEntityType !== 'vehicle') {
    return defaultResult();
  }

  if (isDriverStep(stepName)) {
    const driverIndex = driverEntities.findIndex(
      (entity) => entity.id === selectedEntityId
    );

    return {
      vehicleNum: 'none',
      driverNum: driverIndex !== -1 ? driverIndex + 1 : 'none',
      totalVehicles: vehicleEntities.length,
      totalDrivers: driverEntities.length,
    };
  }

  if (isVehicleStep(stepName)) {
    const vehicleIndex = vehicleEntities.findIndex(
      (entity) => entity.id === selectedEntityId
    );

    return {
      vehicleNum: vehicleIndex !== -1 ? vehicleIndex + 1 : 'none',
      driverNum: 'none',
      totalVehicles: vehicleEntities.length,
      totalDrivers: driverEntities.length,
    };
  }

  return null;
};
